/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useContext, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Flex, Loader } from '@opendoor/bricks/core';
import AccountForm from '@opendoor/cinderblocks/platform/auth/AccountForm';
import { trackEvent } from '@opendoor/shared-fe/build/analytics';

import { SlackChannel, SlackMessageField } from '../../__generated__/internalGlobalTypes';
import { SessionContext } from '../../config/context';
import history from '../../config/history';
import {
  IPostSlackMessage,
  IPostSlackMessageVariables,
  ISendPasswordSetupEmail,
  ISendPasswordSetupEmailVariables,
  ISignUpPartner,
  ISignUpPartnerVariables,
  POST_SLACK_MESSAGE,
  SEND_PASSWORD_SETUP_EMAIL,
  SIGN_UP_PARTNER,
} from '../../graphql/mutations';

type SignUpAccountProps = {
  location?: {
    state?: {
      email: string;
      role: string;
      branchId?: string;
      marketIdentifier: string;
      partnerIdentifier: string;
      otherPartnerIdentifier?: string;
      otherMarketIdentifier?: string;
    };
  };
};

const SignUpAccount: React.FC<SignUpAccountProps> = (props: SignUpAccountProps) => {
  const session = useContext(SessionContext);
  const client = useApolloClient();

  const [hasGeneratedPassword, setHasGeneratedPassword] = useState<boolean>(false);
  const [isNewHuman, setIsNewHuman] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasLoginError, setHasLoginError] = useState<boolean>(false);

  useEffect(() => {
    if (hasLoginError) {
      const { role, partnerIdentifier, email } = props.location!.state!;

      trackEvent.apply(null, [
        'partner-sign-up-flow',
        'login-error',
        partnerIdentifier,
        {
          email,
          role,
        },
      ]);
    }
    // eslint-disable-next-line
  }, [hasLoginError]);

  const buildSignUpSlackMessage = (
    preApprovedAccess: boolean,
    email: string,
    partnerIdentifier: string,
    marketIdentifier: string,
  ): Array<SlackMessageField> => {
    return [
      {
        title: 'Email',
        value: email,
      },
      {
        title: 'Partner',
        value: partnerIdentifier,
      },
      {
        title: 'Market',
        value: marketIdentifier,
      },
      {
        title: 'Pre-Approved Access?',
        value: preApprovedAccess ? 'Yes' : 'No',
      },
    ];
  };

  const handleLogInOrRegister = (): Promise<Array<boolean>> => {
    setHasLoginError(false);
    const {
      role,
      partnerIdentifier,
      marketIdentifier,
      otherPartnerIdentifier,
      otherMarketIdentifier,
      email,
    } = props.location!.state!;

    trackEvent.apply(null, [
      'partner-sign-up-flow',
      'login-success',
      partnerIdentifier,
      {
        email,
        role,
      },
    ]);

    const useOtherMarket = marketIdentifier === 'other';
    let preApprovedAccess = true;

    if (partnerIdentifier === 'other_builder') {
      preApprovedAccess = false;
      const mutation = POST_SLACK_MESSAGE;
      const variables: IPostSlackMessageVariables = {
        channel: SlackChannel.SIGN_UP,
        message: buildSignUpSlackMessage(
          preApprovedAccess,
          email,
          otherPartnerIdentifier!,
          marketIdentifier === 'other' ? otherMarketIdentifier! : marketIdentifier!,
        ),
      };
      return client
        .mutate<IPostSlackMessage, IPostSlackMessageVariables>({ mutation, variables })
        .then(() => {
          return [preApprovedAccess, useOtherMarket];
        });
    }

    const mutation = SIGN_UP_PARTNER;
    const variables: ISignUpPartnerVariables = {
      partnerUser: {
        partnerIdentifier,
        regionIdentifier: marketIdentifier === 'other' ? '' : marketIdentifier,
        email,
        firstName: '', // TODO: make this an optional param in bff schema
        lastName: '', // TODO: make this an optional param in bff schema
        roles: [role],
      },
      channel: SlackChannel.SIGN_UP,
      message: buildSignUpSlackMessage(
        preApprovedAccess,
        email,
        partnerIdentifier,
        marketIdentifier === 'other' ? otherMarketIdentifier! : marketIdentifier!,
      ),
    };

    return client
      .mutate<ISignUpPartner, ISignUpPartnerVariables>({ mutation, variables })
      .then(() => {
        return [preApprovedAccess, useOtherMarket];
      });
  };

  const onLogin = (email: string, password: string) => {
    session
      .logIn(email, password)
      .then(() => {
        setHasLoginError(false);
        return handleLogInOrRegister();
      })
      .then(([preApprovedAccess, outOfMarket]) => {
        if (preApprovedAccess) {
          return history.push('/referral-dashboard');
        }
        return history.push(`/sign-up/complete`, { outOfMarket });
      })
      .catch(() => {
        setHasLoginError(true);
      });
  };

  const onRegister = (email: string, name: string, password: string, phone: string) => {
    setIsLoading(true);
    session
      .register(email, name, password, phone)
      .then(() => {
        setHasLoginError(false);
        return handleLogInOrRegister();
      })
      .then(([preApprovedAccess, outOfMarket]) => {
        if (preApprovedAccess) {
          return history.push('/referral-dashboard');
        }
        return history.push('/sign-up/complete', { outOfMarket });
      })
      .catch(() => {
        return history.push('/error');
      });
  };

  const onHumanStatusRequest = (email: string) => {
    session.requestHumanStatus(email).then(handleHumanStatusResponse).catch(handleHumanStatusError);
  };

  useEffect(() => {
    if (props.location && props.location.state && props.location.state.email) {
      session
        .requestHumanStatus(props.location.state.email)
        .then(handleHumanStatusResponse)
        .catch(handleHumanStatusError);
    }
    // eslint-disable-next-line
  }, []);

  const handleHumanStatusResponse = (resp: any) => {
    const { has_generated_password } = resp;
    const { email } = props.location!.state!;
    setHasGeneratedPassword(has_generated_password);
    if (has_generated_password) {
      const mutation = SEND_PASSWORD_SETUP_EMAIL;
      const variables = {
        email,
      };
      client.mutate<ISendPasswordSetupEmail, ISendPasswordSetupEmailVariables>({
        mutation,
        variables,
      });
    }
    setIsLoading(false);
  };

  const handleHumanStatusError = () => {
    setIsNewHuman(true);
    setIsLoading(false);
  };

  return (
    <Flex width="100%" justifyContent="center">
      {isLoading ? (
        <Loader />
      ) : (
        <AccountForm
          /* 
           Cinderblocks is pinned to an old version in Partnership-Dashboard-fe that doesn't support the latest Account Form,
           this will be updated at a separate time when Parternship is compatible with Bricks-next and latest version of Cinderblocks.
           */
          showNovoStyle={false}
          analyticsName="partnership-dashboard-fe-account-form"
          email={props.location!.state!.email}
          error={hasLoginError}
          isNewHuman={isNewHuman}
          hasGeneratedPassword={hasGeneratedPassword}
          onLogin={onLogin}
          onRegister={onRegister}
          onHumanStatusRequest={onHumanStatusRequest}
        />
      )}
    </Flex>
  );
};

export default SignUpAccount;
